import { useTranslation } from 'react-i18next';
import { UserCircle } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';

import { OliveIconWhite, OliveIconSmall } from '../components/OliveIcon';
import { useEnrollmentProfileQuery } from 'enrollment/queries';
import { displayDependentAvatar } from 'enrollment/features/Family/FamilyDetailsPage/components';
import { useOliveAdvisorChatQuery } from 'enrollment/queries/useOliveAdvisorQuery';
import './OliveDrawer.css';

export interface OliveDrawerProps {
  userQuery?: string;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export const OliveDrawer: React.FC<OliveDrawerProps> = ({
  userQuery,
  loading,
  setLoading,
}) => {
  const { t } = useTranslation(['oliveAdvisor', 'common']);
  const isMobile = window.innerWidth < 744;

  const { queryOlive } = useOliveAdvisorChatQuery();
  const { subscriber } = useEnrollmentProfileQuery({ lumosEnabled: true });
  const subscriberProfile = subscriber?.data?.subscriber_profile;

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [lastSentQuery, setLastSentQuery] = useState<string | null>(null);

  const isPlansPage = window.location.pathname.includes('/plans');

  function showResponse(input: { message: string } | { response: string }) {
    const message = 'message' in input ? input.message : input.response;
    setChatMessages((prevMessages) => {
      const updatedMessages = [...prevMessages];
      updatedMessages[updatedMessages.length - 1] = {
        role: 'assistant',
        content: `<div class="olive-response-content fade-in">${message}</div>`,
      };
      setLoading(false);
      return updatedMessages;
    });
  }

  const handleNewQuery = (query: string) => {
    setChatMessages((prevMessages) => [
      ...prevMessages,
      { role: 'user', content: query },
      { role: 'assistant', content: '<div class="dot-flashing"></div>' },
    ]);
    setLoading(true);

    if (isPlansPage) {
      queryOlive(query)
        .then((response) => {
          if (!response) {
            showResponse({ message: t('recommend.error') });
          } else {
            showResponse({ response });

            // Remove the container after animation completes
            setTimeout(() => {
              setChatMessages((prevMessages) => {
                const updatedMessages = [...prevMessages];
                updatedMessages[updatedMessages.length - 1] = {
                  ...updatedMessages[updatedMessages.length - 1],
                  content: response,
                };
                return updatedMessages;
              });
              setLoading(false);
            }, 250); // Match the animation duration
          }
        })
        .catch((error) => {
          console.error('Error fetching Olive response:', error);
          showResponse({ message: t('recommend.error') });
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userQuery && userQuery !== lastSentQuery) {
      handleNewQuery(userQuery);
      setLastSentQuery(userQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userQuery, lastSentQuery, queryOlive]);

  const handleClose = () => {
    const drawer = document.getElementById('olive-drawer') as HTMLDialogElement;
    if (drawer) {
      drawer.classList.add('closing');
      setTimeout(() => {
        drawer.classList.remove('open', 'closing');
        drawer.close();
        document.documentElement.style.overflow = 'auto'; // Reset overflow style on close
      }, 250); // Match the transition duration
    }
  };

  useEffect(() => {
    const drawer = document.getElementById('olive-drawer') as HTMLDialogElement;

    const handleEscClose = () => {
      document.documentElement.style.overflow = 'auto';
      drawer.style.display = '';
    };

    if (drawer) {
      drawer.addEventListener('close', handleEscClose);
    }

    return () => {
      if (drawer) {
        drawer.removeEventListener('close', handleEscClose);
      }
    };
  }, []);

  useEffect(() => {
    const scrollToLastMessage = () => {
      const chatContent = document.getElementById('advisor-chat-content');
      const oliveReplies =
        chatContent?.getElementsByClassName('olive-reply grid-8');

      if (chatContent && oliveReplies?.length) {
        const lastOliveReply = oliveReplies[
          oliveReplies.length - 1
        ] as HTMLElement;
        chatContent.scrollTo({
          top: lastOliveReply.offsetTop - chatContent.offsetTop - 50,
          behavior: 'smooth',
        });
      }
    };

    // Trigger scroll when chatMessages update
    scrollToLastMessage();

    // Check drawer visibility and scroll when it opens
    const drawer = document.getElementById('olive-drawer') as HTMLDialogElement;

    const observer = new MutationObserver(() => {
      if (drawer && drawer.style.display !== 'none') {
        scrollToLastMessage();
      }
    });

    // Observe the drawer for style changes
    if (drawer) {
      observer.observe(drawer, {
        attributes: true,
        attributeFilter: ['style'],
      });
    }

    // Clean up the observer on component unmount
    return () => observer.disconnect();
  }, [chatMessages]);

  const UserChat = ({ content }: { content: string }) => (
    <div className={`user-chat ${isMobile ? '' : 'grid-8'}`}>
      <div className="user-message">{content}</div>
      <div className="user-icon">
        {subscriberProfile ? (
          displayDependentAvatar(subscriberProfile, 40)
        ) : (
          <UserCircle size={40} alt={t('drawer.you')} />
        )}
        <span className="sr-only">{t('drawer.you')}</span>
      </div>
    </div>
  );

  const OliveAnswer = ({ content }: { content: string }) => (
    <>
      <div className={`olive-reply ${isMobile ? '' : 'grid-8'}`}>
        <div className="olive-image">
          <OliveIconSmall />
        </div>
        <div className="response-data">
          <div
            className="olive-response"
            dangerouslySetInnerHTML={{ __html: content }}
          ></div>
        </div>
      </div>
      <Disclaimer />
    </>
  );

  const Disclaimer = () => (
    <div className={isMobile ? '' : 'grid-8'}>
      <div className="response-disclaimer">
        <div>{t('drawer.verify')}</div>
        <div className="helpful">
          <div>Was this helpful?</div>
          <div
            className="btn-group"
            role="group"
            aria-label="Was this helpful?"
          >
            <button type="button" className="btn">
              <div
                className="thumbs-up"
                aria-hidden="true"
                role="presentation"
              ></div>
              <span className="sr-only">{t('yes', { ns: 'common' })}</span>
            </button>
            <button type="button" className="btn">
              <div
                className="thumbs-down"
                aria-hidden="true"
                role="presentation"
              ></div>
              <span className="sr-only">{t('no', { ns: 'common' })}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const ChatBar = () => {
    const [inputValue, setInputValue] = useState('');

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    };

    const handleFormSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (inputValue.trim()) {
        handleNewQuery(inputValue.trim()); // This sends the query only once on submit
        setInputValue(''); // Reset input field
      }
    };

    return (
      <div id="chat-bar">
        <form onSubmit={handleFormSubmit}>
          <div className="select-wrap">
            <input
              type="text"
              placeholder={t('drawer.placeholder')}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && loading) {
                  e.preventDefault();
                }
              }}
            />
            <button
              className="btn btn-icon btn-link click-icon"
              id="chat-bar-submit"
              type="submit"
              disabled={loading} // Disable button during loading
            >
              <i className="ph ph-paper-plane-tilt"></i>
              <span className="sr-only">{t('drawer.send')}</span>
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <dialog id="olive-drawer">
      <header>
        <div className="d-flex justify-content-end">
          <button
            className="btn btn-icon btn-link"
            autoFocus
            onClick={handleClose}
          >
            <i className="ph-bold ph-x"></i>
            <span className="sr-only">{t('close', { ns: 'common' })}</span>
          </button>
        </div>
        <h2>
          <OliveIconWhite /> {t('drawer.title')}
        </h2>
        <p>{isMobile ? t('drawer.subtitleMobile') : t('drawer.subtitle')}</p>
      </header>
      <div id="advisor-chat-content">
        {chatMessages.map((message, index) =>
          message.role === 'user' ? (
            <UserChat key={index} content={message.content} />
          ) : (
            <OliveAnswer key={index} content={message.content} />
          )
        )}
      </div>
      <ChatBar />
    </dialog>
  );
};
